import React, {useContext, useMemo, useRef, useState} from 'react'
import {makeStyles, Table, TableBody, TableCell, TableHead, TableRow} from '@material-ui/core';
import Tr from 'translations/Translations';
import BoldTypography from "components/BoldTypography";
import PropTypes from 'prop-types';
import OfferEspdTableRow from "./OfferEspdTableRow";
import {RoundType} from "constants/RoundType";
import OfferSectionContext from "context/OfferSectionContext";
import PurchaserOfferContext from "context/PurchaserOfferContext";
import {SignatureType} from "constants/SignatureType";
import DemandOffersOfferService from "services/DemandOffersOfferService";
import {simpleAlert} from "libs/AlertNotification";
import useSignature from "hooks/useSignature";
import useLoadingScreen from "hooks/useLoadingScreen";
import {useParams} from "react-router";
import {OfferMode} from "../../../../common/constants/OfferMode";
import VS from "constants/ValidationShema";
import MultiButton, {createButton} from "components/popup/components/MultiButton";
import Form from "components/Form";
import SaveIcon from "@material-ui/icons/Save";
import EditIcon from "@material-ui/icons/Edit";

const useStyle = makeStyles(theme => ({
    buttonColumn: theme.components.table.buttonColumn,
    dateColumn: theme.components.table.dateColumn,
    typeColumn: {
        width: 180,
        '& .MuiFormHelperText-root': {
            display: 'none'
        }
    }
}));

export const CONTRACTOR_OFFER_EDIT_ESPD_FILES_FORM_ID = "cont_offer_edit_espd_files";

function OfferEspdTable(
    {
        disabled,
        mode,
        section,
        offerEdit,
        roundType,
    }) {

    const classes = useStyle();
    const contextSection = useContext(OfferSectionContext);
    const purchaserOfferContext = useContext(PurchaserOfferContext);
    const signFileRef = useRef(null);
    const params = useParams();
    const {doSignDocContent, breakSigning} = useSignature();
    const {hideLoadingScreen, showLoadingScreen} = useLoadingScreen();
    const [bulkEdit, setBulkEdit] = useState(false);
    const [bulkEditWorking, setBulkEditWorking] = useState(false);
    const positionIdToOrderMap = useMemo(() => offerEdit.positions.reduce((prev, cur) => {
        prev[`positionId_${cur.positionId}`] = cur.order;
        return prev;
    }, {}), [offerEdit.positions]);

    const signFileAvailable = useMemo(() => !purchaserOfferContext && offerEdit.espdFiles.some(x => x.signatureConfiguration !== null), [offerEdit.espdFiles, purchaserOfferContext]);

    const doBreakSigning = () => {
        signFileRef.current = null;
        breakSigning();
    }

    const onSignDocument = (row) => {
        if (signFileRef.current != null) {
            return;
        }
        signFileRef.current = row;

        doSignDocContent(
            section.signatureTypes.filter(x => [SignatureType.QUALIFIED, SignatureType.PERSONAL_ID].includes(x)),
            async (sigType) => {
                let fileUrl;
                if (roundType === RoundType.BASE) {
                    fileUrl = await DemandOffersOfferService.getDownloadEspdFileUrl(params.id, params.stageId, mode, row.identity, purchaserOfferContext?.supplierId);
                } else {
                    console.error("Sign document unavilable for roundType: +" + roundType)
                }
                if (!fileUrl) {
                    breakSigning();
                    return;
                }
                const result = await DemandOffersOfferService.getDocumentBase64EncodedToSignDocument(fileUrl);
                if (!result)
                    doBreakSigning();
                return result;

            },
            (signatureType, signature) => {
                doBreakSigning();
                showLoadingScreen();
                const data = {signatureType, signature}
                if (roundType === RoundType.BASE) {
                    DemandOffersOfferService.signEspdFile(params.id, params.stageId, row.identity, data)
                        .then((resp) => {
                            if (resp === true) simpleAlert("sign_document_completed")
                        }).finally(hideLoadingScreen);
                }
            },
            (error) => {
                console.error(error)
                simpleAlert("signature_error");
                doBreakSigning();
            },
            () => {
                doBreakSigning();
            },
            async () => contextSection.encryptionEnabled ? contextSection.roundId : null,
            row.signatureConfiguration
        )
    }

    const onBulkSubmit = ({form}, actions) => {
        setBulkEditWorking(true);
        const filtered = form.filter((x, i) => offerEdit.espdFiles[i].documentType !== x.documentType || offerEdit.espdFiles[i].containsRodo !== x.containsRodo).map(x => ({
            fileId: x.identity,
            documentType: x.documentType,
            containsRodo: x.containsRodo
        }))

        if (filtered.length > 0) {
            let promise;
            switch (roundType) {
                case RoundType.BASE:
                    promise = DemandOffersOfferService.editEspdFiles(params.id, params.stageId, mode, purchaserOfferContext?.supplierId, {files: filtered})
                    break;
                default:
                    throw new Error("Unsupported roundType: " + roundType)
            }

            promise.finally(() => {
                setBulkEdit(false);
                setBulkEditWorking(false);
            })
        } else {
            setBulkEdit(false);
            setBulkEditWorking(false);
        }
    }

    const getButtons = (formikProps) => {
        const buttons = [];
        if (bulkEdit) {
            buttons.push(createButton(Tr('discard_changes'), () => {
                formikProps.resetForm();
                setBulkEdit(false);
            }, "outlined", bulkEditWorking, undefined, false, undefined));

            buttons.push(createButton(Tr('operator.modify.update_files'), () => formikProps.submitForm(),
                undefined, bulkEditWorking, undefined, bulkEditWorking, SaveIcon));

        } else {
            buttons.push(createButton(Tr('change_files_type'), () => setBulkEdit(true),
                undefined, disabled || bulkEdit, undefined, false, EditIcon));
        }
        return buttons;
    }

    return (<div>
        {!offerEdit.espdFiles.length ?
            <BoldTypography align='center' variant="h6">{Tr('no_options')}</BoldTypography>
            :
            <Form
                id={CONTRACTOR_OFFER_EDIT_ESPD_FILES_FORM_ID}
                initialValues={{
                    form: offerEdit.espdFiles
                }}
                validationSchema={VS.any()}
                onSubmit={onBulkSubmit}
            >{(formikProps) => (<>
                {
                    (purchaserOfferContext && mode === OfferMode.PLACED && roundType === RoundType.BASE) &&
                    <MultiButton buttons={getButtons(formikProps)}/>
                }

                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{Tr("name")}</TableCell>
                            <TableCell className={classes.dateColumn}>{Tr("add_data")}</TableCell>
                            <TableCell className={classes.typeColumn}>{Tr("contains_rodo")}</TableCell>
                            <TableCell className={classes.typeColumn}>{Tr("type")}</TableCell>
                            <TableCell className={classes.buttonColumn}>{Tr("size")}</TableCell>
                            {offerEdit.espdOnPositions && <TableCell className={classes.dateColumn}>{Tr("assignment")}</TableCell>}
                            {(!purchaserOfferContext && (!!contextSection.digitalSign || offerEdit.passiveOfferForm)) &&
                                <TableCell colSpan={signFileAvailable ? 2 : 1} className={classes.buttonColumn}>{Tr("file_sign_status")}</TableCell>}
                            <TableCell className={classes.buttonColumn}></TableCell>
                            {!(disabled || mode === OfferMode.PLACED)  && (!purchaserOfferContext || mode === OfferMode.EDIT) && <TableCell className={classes.buttonColumn}></TableCell>}
                            {!(disabled || mode === OfferMode.PLACED) && <TableCell className={classes.buttonColumn}></TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            offerEdit.espdFiles.map((doc, index) => (
                                <OfferEspdTableRow
                                    classes={classes}
                                    key={doc.identity}
                                    row={doc}
                                    disabled={disabled}
                                    espdOnPositions={offerEdit.espdOnPositions}
                                    positionIdToOrderMap={positionIdToOrderMap}
                                    mode={mode}
                                    roundType={roundType}
                                    passiveOfferForm={offerEdit.passiveOfferForm}
                                    signFileAvailable={signFileAvailable}
                                    onSignDocument={onSignDocument}
                                    bulkEdit={bulkEdit}
                                    formikProps={formikProps}
                                    index={index}
                                />))
                        }
                    </TableBody>
                </Table>
            </>)}
            </Form>
        }
    </div>)
}

OfferEspdTable.propTypes = {
    offerEdit: PropTypes.shape({
        offerFiles: PropTypes.array.isRequired,
        positions: PropTypes.array.isRequired
    }).isRequired,
    roundType: PropTypes.oneOf(Object.keys(RoundType)).isRequired
};
export default OfferEspdTable;